// Step 4's AI-powered "kiểm tra & tối ưu" check.
//
// Frontend stub for now — clicking the button reveals a hardcoded list
// of suggestions that read realistically against any draft. Once a real
// `/ai/quality-check` endpoint exists this component swaps to the live
// mutation without changing its public shape.

import { useState } from "react";
import { Check, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import type { PropertyDraft } from "@/features/property/wizard/wizard-state";

interface QualityFinding {
  kind: "good" | "warn";
  message: string;
}

interface AiQualityCheckProps {
  draft: PropertyDraft;
}

export function AiQualityCheck({ draft }: AiQualityCheckProps) {
  const [findings, setFindings] = useState<QualityFinding[] | null>(null);

  const runStubbedCheck = () => {
    setFindings(buildStubFindings(draft));
  };

  return (
    <section
      aria-label="AI kiểm tra"
      className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-card p-5"
    >
      <header className="mb-3 flex items-center justify-between">
        <h3 className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary">
          <Sparkles aria-hidden="true" className="size-4" />
          AI kiểm tra & tối ưu
        </h3>
        <button
          type="button"
          onClick={runStubbedCheck}
          className={cn(
            "rounded-md px-3 py-1 text-[12px] font-semibold text-primary-foreground transition-colors",
            findings !== null ? "bg-success" : "bg-primary hover:bg-primary/90",
          )}
        >
          {findings !== null ? "✓ Đã phân tích" : "Chạy AI"}
        </button>
      </header>

      {findings === null ? (
        <p className="text-[12px] text-muted-foreground">
          AI sẽ kiểm tra nội dung, đề xuất cải thiện mô tả và tối ưu cho tìm
          kiếm.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {findings.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-[12px] text-ink-soft"
            >
              {f.kind === "good" ? (
                <Check
                  aria-hidden="true"
                  className="mt-0.5 size-3.5 shrink-0 text-success"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="mt-0.5 inline-flex size-3.5 shrink-0 items-center justify-center font-bold text-warning"
                >
                  !
                </span>
              )}
              {f.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Stubbed quality findings
//
// Generates suggestions that look plausible against the user's draft —
// real AI endpoint can drop in here later without changing the props.
// ---------------------------------------------------------------------------

function buildStubFindings(draft: PropertyDraft): QualityFinding[] {
  const findings: QualityFinding[] = [];

  if (draft.buildingName && draft.buildingName.length > 4) {
    findings.push({
      kind: "good",
      message: "Tên toà nhà rõ ràng, dễ nhận diện.",
    });
  } else {
    findings.push({
      kind: "warn",
      message: "Cân nhắc thêm tên toà nhà / dự án để khách dễ tìm kiếm.",
    });
  }

  if (draft.price !== undefined) {
    findings.push({
      kind: "good",
      message: "Giá phù hợp phân khúc — chấm theo dữ liệu cùng khu vực.",
    });
  }

  if (draft.description && draft.description.length > 80) {
    findings.push({
      kind: "good",
      message: "Mô tả đủ chi tiết để SEO tốt.",
    });
  } else {
    findings.push({
      kind: "warn",
      message: "Mô tả còn ngắn — nên có ít nhất 80 ký tự kèm từ khoá địa danh.",
    });
  }

  findings.push({
    kind: "warn",
    message: "Nên thêm video 360° để tăng tỉ lệ hẹn xem.",
  });

  return findings;
}
